import { inboxApi } from "@/api/inbox-api";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import type { IInboundEmail } from "@/types/data-models";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow, isToday, isYesterday } from "date-fns";
import { ArrowLeft, Mail, Paperclip } from "lucide-react";
import { memo, useMemo, useState } from "react";
import { Link } from "react-router";
import { FilePreviewDialog } from "@/components/common/file-preview-dialog";

interface EmailsTabProps {
  currentDeal: DealTransformed;
}

function EmailsTab({ currentDeal }: EmailsTabProps) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const [selectedEmail, setSelectedEmail] = useState<IInboundEmail | null>(
    null
  );

  const { data, isLoading } = useQuery({
    queryKey: ["deal-emails", activeWorkspaceId, currentDeal.id],
    queryFn: async () => {
      const response = await inboxApi.listEmails(activeWorkspaceId!, "linked");
      // Filter to emails linked to this deal. currentDeal.id is the
      // applicant_company id (per DealTransformed); the BE populates
      // linked_company against the Company ref so we match on that.
      return response.emails.filter(
        (email) => email.linked_company?._id === currentDeal.id
      );
    },
    enabled: !!activeWorkspaceId && !!currentDeal.id,
  });

  const emails = data ?? [];
  const groups = useMemo(() => groupByDate(emails), [emails]);

  if (isLoading) {
    return <EmailsSkeleton />;
  }

  if (emails.length === 0) {
    return <EmailsEmptyState workspaceId={activeWorkspaceId} />;
  }

  if (selectedEmail) {
    return (
      <EmailDetail
        email={selectedEmail}
        onBack={() => setSelectedEmail(null)}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-6">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-foreground">
          {emails.length} {emails.length === 1 ? "email" : "emails"}
        </h2>
        <span className="text-xs text-muted-foreground">
          Linked from Inbox
        </span>
      </div>

      <div className="space-y-6">
        {groups.map(({ label, items }) => (
          <section key={label}>
            <div className="mb-2 px-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">
              {label}
            </div>
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              {items.map((email, i) => (
                <EmailRow
                  key={email._id}
                  email={email}
                  isLast={i === items.length - 1}
                  onClick={() => setSelectedEmail(email)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function EmailRow({
  email,
  isLast,
  onClick,
}: {
  email: IInboundEmail;
  isLast: boolean;
  onClick: () => void;
}) {
  const senderLabel = email.from_name || email.from_email;
  const snippet = bodySnippet(email);

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-foreground/[0.03]",
        !isLast && "border-b border-border"
      )}
    >
      <SenderAvatar name={senderLabel} email={email.from_email} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center justify-between gap-3">
          <span className="truncate text-sm font-medium text-foreground">
            {senderLabel}
          </span>
          <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/70">
            {formatDistanceToNow(new Date(email.received_at), {
              addSuffix: true,
            })}
          </span>
        </div>
        <p className="truncate text-xs text-foreground/80">
          {email.subject || "(No subject)"}
        </p>
        {snippet && (
          <p className="truncate text-xs text-muted-foreground">{snippet}</p>
        )}
      </div>
      {email.attachments.length > 0 && (
        <div className="flex shrink-0 items-center gap-1 self-center text-[10px] text-muted-foreground/70">
          <Paperclip className="h-3 w-3" />
          {email.attachments.length}
        </div>
      )}
    </button>
  );
}

function EmailDetail({
  email,
  onBack,
}: {
  email: IInboundEmail;
  onBack: () => void;
}) {
  const senderLabel = email.from_name || email.from_email;
  const receivedAt = new Date(email.received_at);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="sticky top-0 z-10 shrink-0 border-b border-border bg-background px-6 py-3">
        <button
          onClick={onBack}
          className="mb-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to emails
        </button>
        <h1 className="text-lg font-semibold tracking-tight text-foreground">
          {email.subject || "(No subject)"}
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-6 py-6">
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-border bg-card p-4">
            <SenderAvatar
              name={senderLabel}
              email={email.from_email}
              size="lg"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-semibold text-foreground">
                {senderLabel}
              </span>
              {email.from_name && email.from_email && (
                <span className="truncate font-mono text-xs text-muted-foreground">
                  {email.from_email}
                </span>
              )}
              <span
                className="text-xs text-muted-foreground/80"
                title={format(receivedAt, "PPpp")}
              >
                {format(receivedAt, "MMM d, yyyy 'at' h:mm a")}
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            {email.html ? (
              <div
                className="prose prose-sm max-w-none text-foreground/85"
                dangerouslySetInnerHTML={{ __html: email.html }}
              />
            ) : (
              <p className="whitespace-pre-wrap text-sm text-foreground/85">
                {email.text || "No content"}
              </p>
            )}
          </div>

          {email.attachments.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 px-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">
                {email.attachments.length}{" "}
                {email.attachments.length === 1
                  ? "attachment"
                  : "attachments"}
              </h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {email.attachments.map((att, i) => (
                  <FilePreviewDialog
                    key={i}
                    url={att.file_url}
                    fileName={att.file_name}
                    fileId={att.file_upload_id}
                  >
                    <button
                      type="button"
                      className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 text-left transition-colors hover:bg-foreground/[0.02]"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-border bg-muted/40">
                        <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-xs font-medium text-foreground">
                          {att.file_name}
                        </span>
                        {att.file_size && (
                          <span className="text-[10px] tabular-nums text-muted-foreground/70">
                            {formatFileSize(att.file_size)}
                          </span>
                        )}
                      </div>
                    </button>
                  </FilePreviewDialog>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmailsEmptyState({ workspaceId }: { workspaceId: string | null }) {
  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card py-16 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-border bg-muted/40">
          <Mail className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-foreground">
          No emails linked to this deal
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Emails linked from your Inbox will appear here.
        </p>
        {workspaceId && (
          <Link
            to={`/${workspaceId}/inbox`}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-foreground/[0.03]"
          >
            Open Inbox
          </Link>
        )}
      </div>
    </div>
  );
}

function EmailsSkeleton() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-6">
      <div className="mb-4 h-4 w-24 animate-pulse rounded bg-muted/50" />
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              "flex items-start gap-3 px-4 py-3",
              i < 2 && "border-b border-border"
            )}
          >
            <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted/50" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="h-3 w-32 animate-pulse rounded bg-muted/50" />
              <div className="h-3 w-48 animate-pulse rounded bg-muted/40" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-muted/30" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SenderAvatar({
  name,
  email,
  size = "sm",
}: {
  name: string;
  email: string;
  size?: "sm" | "lg";
}) {
  const initials = getInitials(name || email);
  const colorClass = avatarColor(email);
  const dim = size === "lg" ? "h-10 w-10 text-sm" : "h-8 w-8 text-xs";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-semibold",
        dim,
        colorClass
      )}
    >
      {initials}
    </div>
  );
}

// --- helpers --------------------------------------------------------------

const AVATAR_PALETTE = [
  "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
];

function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

function getInitials(input: string): string {
  const cleaned = input.split("@")[0].trim();
  const parts = cleaned.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]!.toUpperCase();
  return (parts[0][0]! + parts[parts.length - 1][0]!).toUpperCase();
}

function bodySnippet(email: IInboundEmail): string {
  const source = email.text || stripHtml(email.html ?? "");
  return source.replace(/\s+/g, " ").trim().slice(0, 140);
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ");
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type EmailGroup = { label: string; items: IInboundEmail[] };

function groupByDate(emails: IInboundEmail[]): EmailGroup[] {
  const buckets: Record<string, IInboundEmail[]> = {
    Today: [],
    Yesterday: [],
    "Last 7 days": [],
    Earlier: [],
  };
  const now = Date.now();
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

  for (const email of emails) {
    const d = new Date(email.received_at);
    if (isToday(d)) buckets.Today.push(email);
    else if (isYesterday(d)) buckets.Yesterday.push(email);
    else if (now - d.getTime() < SEVEN_DAYS) buckets["Last 7 days"].push(email);
    else buckets.Earlier.push(email);
  }

  // Preserve order, drop empty buckets, sort each bucket newest-first.
  return (Object.keys(buckets) as Array<keyof typeof buckets>)
    .map((label) => ({
      label,
      items: buckets[label].sort(
        (a, b) =>
          new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
      ),
    }))
    .filter((g) => g.items.length > 0);
}

export default memo(EmailsTab);
